import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { projectAPI, userAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';
import { FiPlus, FiFolder, FiX, FiSearch, FiGrid, FiList, FiCalendar, FiCheckCircle, FiAlertCircle, FiActivity } from 'react-icons/fi';

const STATUS_COLORS = {
  active:    { bg:'#d1fae5', txt:'#065f46' },
  planning:  { bg:'#ede9fe', txt:'#5b21b6' },
  'on-hold': { bg:'#fef3c7', txt:'#92400e' },
  completed: { bg:'#dbeafe', txt:'#1e40af' },
  cancelled: { bg:'#fee2e2', txt:'#991b1b' },
};
const PRIORITY_COLORS = { high:'#ef4444', medium:'#f59e0b', low:'#22c55e' };
const CLIENT_TYPE_LABELS = {
  direct_india_no_gst:'Direct Indian Client (No GST)', direct_india_gst:'Direct Indian Client (GST)',
  upwork_personal:'Upwork Personal', upwork_company:'Upwork Company',
  wise:'Wise', paypal:'PayPal', referral:'Referral', other:'Other',
};
const CLIENT_TYPE_ICONS = {
  direct_india_no_gst:'🇮🇳', direct_india_gst:'🧾',
  upwork_personal:'💼', upwork_company:'🏢',
  wise:'💳', paypal:'🅿️', referral:'🤝', other:'🔖',
};

function TeamAvatars({ members, limit = 3 }) {
  if (!members?.length) return <span style={{ fontSize:12, color:'#94a3b8' }}>No members</span>;
  const colors = ['#6366f1','#f59e0b','#10b981','#ef4444','#8b5cf6','#3b82f6'];
  return (
    <div style={{ display:'flex', alignItems:'center', gap:6 }}>
      <div style={{ display:'flex' }}>
        {members.slice(0, limit).map((m, idx) => {
          const initials = (m.name||'?').split(' ').map(w=>w[0]).join('').toUpperCase().slice(0,2);
          return (
            <div key={m.id||idx} title={m.name} style={{
              width:24, height:24, borderRadius:'50%',
              background: colors[idx % colors.length],
              border:'2px solid #fff', marginLeft: idx===0?0:-7,
              display:'flex', alignItems:'center', justifyContent:'center',
              fontSize:9, fontWeight:700, color:'#fff',
              position:'relative', zIndex:10-idx, overflow:'hidden', flexShrink:0,
            }}>
              {m.avatar
                ? <img src={m.avatar} alt={m.name} style={{ width:'100%', height:'100%', objectFit:'cover' }}/>
                : initials}
            </div>
          );
        })}
        {members.length > limit && (
          <div style={{
            width:24, height:24, borderRadius:'50%', background:'#e2e8f0',
            border:'2px solid #fff', marginLeft:-7,
            display:'flex', alignItems:'center', justifyContent:'center',
            fontSize:9, fontWeight:700, color:'#64748b', position:'relative',
          }}>+{members.length - limit}</div>
        )}
      </div>
      <span style={{ fontSize:11, color:'#64748b' }}>
        {members.length <= 2
          ? members.map(m=>m.name?.split(' ')[0]).join(', ')
          : `${members[0].name?.split(' ')[0]} +${members.length-1}`}
      </span>
    </div>
  );
}

export default function Projects() {
  const { user } = useAuth();
  const isAdmin  = user?.role === 'admin' || user?.role === 'manager';
  const isClient = user?.role === 'client';

  const [projects, setProjects] = useState([]);
  const [clients,  setClients]  = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [search,   setSearch]   = useState('');
  const [filter,   setFilter]   = useState('all');
  const [view,     setView]     = useState('month');
  const [modal,      setModal]      = useState(false);
  const [isInternal, setIsInternal] = useState(false);
  const [form, setForm] = useState({
    name:'', description:'', projectType:'web', status:'planning',
    priority:'medium', budget:'', startDate:'', dueDate:'', clientId:'',
  });

  useEffect(() => { load(); }, []); // eslint-disable-line

  const load = async () => {
    try {
      const [pR, uR] = await Promise.all([
        projectAPI.getAll(),
        (user?.role==='admin'||user?.role==='manager')
          ? userAPI.getAll()
          : Promise.resolve({ data:{ users:[] } }),
      ]);
      setProjects(pR.data.projects || []);
      setClients((uR.data.users||[]).filter(u=>u.role==='client'));
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  };

  const createProject = async () => {
    if (!form.name.trim())           return toast.error('Project name required');
    if (!isInternal && !form.clientId) return toast.error('Select a client or mark as Internal');
    try {
      await projectAPI.create({ ...form, clientId: isInternal ? null : form.clientId });
      toast.success('Project created!');
      setModal(false);
      setIsInternal(false);
      setForm({ name:'', description:'', projectType:'web', status:'planning', priority:'medium', budget:'', startDate:'', dueDate:'', clientId:'' });
      load();
    } catch(e) { toast.error(e.response?.data?.message || 'Failed'); }
  };

  const now = new Date();

  const stats = useMemo(() => ({
    total:     projects.length,
    active:    projects.filter(p=>p.status==='active').length,
    completed: projects.filter(p=>p.status==='completed').length,
    planning:  projects.filter(p=>p.status==='planning').length,
    overdue:   projects.filter(p=>p.dueDate&&new Date(p.dueDate)<now&&!['completed','cancelled'].includes(p.status)).length,
  }), [projects]); // eslint-disable-line

  const filtered = projects.filter(p => {
    const matchSearch = (p.name||'').toLowerCase().includes(search.toLowerCase());
    let matchFilter = true;
    if (filter==='this-week') {
      const ws = new Date(now); ws.setDate(now.getDate()-now.getDay()); ws.setHours(0,0,0,0);
      matchFilter = new Date(p.createdAt) >= ws;
    } else if (filter==='this-month') {
      matchFilter = new Date(p.createdAt) >= new Date(now.getFullYear(), now.getMonth(), 1);
    } else if (filter==='internal') {
      matchFilter = !p.clientId;
    } else {
      matchFilter = filter==='all' || p.status===filter;
    }
    return matchSearch && matchFilter;
  });

  // Group by month (createdAt)
  const byMonth = useMemo(() => {
    const groups = {};
    filtered.forEach(p => {
      const d = new Date(p.createdAt);
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`;
      const label = d.toLocaleDateString('en-IN', { month:'long', year:'numeric' });
      if (!groups[key]) groups[key] = { label, projects:[] };
      groups[key].projects.push(p);
    });
    return Object.keys(groups).sort((a,b)=>b.localeCompare(a)).map(k=>groups[k]);
  }, [filtered]);

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh' }}>
      <div style={{ width:40, height:40, border:'3px solid #e2e8f0', borderTop:'3px solid #6366f1', borderRadius:'50%', animation:'spin 1s linear infinite' }}/>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  const S = {
    btn: (v, sm) => ({
      padding: sm ? '7px 14px' : '10px 20px',
      borderRadius:10, border:'none', fontWeight:600,
      fontSize: sm ? 12 : 14, cursor:'pointer',
      display:'inline-flex', alignItems:'center', gap:6, transition:'all .2s',
      ...(v==='primary'
        ? { background:'var(--pm-gradient)', color:'#fff', boxShadow:'0 4px 12px rgba(99,102,241,.25)' }
        : { background:'#f8fafc', color:'#64748b', border:'1.5px solid #e2e8f0' }),
    }),
    inp: { width:'100%', padding:'9px 13px', border:'2px solid #e2e8f0', borderRadius:9, fontSize:14, outline:'none', boxSizing:'border-box', marginTop:4 },
    overlay: { position:'fixed', inset:0, background:'#00000060', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000, padding:20 },
    modal: { background:'#fff', borderRadius:20, padding:32, width:'100%', maxWidth:560, maxHeight:'90vh', overflowY:'auto' },
    label: { display:'block', fontSize:13, fontWeight:600, color:'#475569', marginBottom:14 },
  };

  const statCards = isClient
    ? [
        { label:'Total',     value:stats.total,     color:'#6366f1', bg:'#eef2ff', icon:<FiFolder size={18}/> },
        { label:'Active',    value:stats.active,    color:'#10b981', bg:'#d1fae5', icon:<FiActivity size={18}/> },
        { label:'Completed', value:stats.completed, color:'#3b82f6', bg:'#dbeafe', icon:<FiCheckCircle size={18}/> },
        { label:'Overdue',   value:stats.overdue,   color:'#ef4444', bg:'#fee2e2', icon:<FiAlertCircle size={18}/> },
      ]
    : [
        { label:'Total',     value:stats.total,     color:'#6366f1', bg:'#eef2ff', icon:<FiFolder size={18}/> },
        { label:'Active',    value:stats.active,    color:'#10b981', bg:'#d1fae5', icon:<FiActivity size={18}/> },
        { label:'Planning',  value:stats.planning,  color:'#8b5cf6', bg:'#ede9fe', icon:<FiCalendar size={18}/> },
        { label:'Completed', value:stats.completed, color:'#3b82f6', bg:'#dbeafe', icon:<FiCheckCircle size={18}/> },
        { label:'Overdue',   value:stats.overdue,   color:'#ef4444', bg:'#fee2e2', icon:<FiAlertCircle size={18}/> },
      ];

  return (
    <div style={{ maxWidth:1280, margin:'0 auto' }}>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}} .proj-row:hover{background:#f8fafc!important}`}</style>

      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
        <div>
          <h1 style={{ margin:0, fontSize:26, fontWeight:800, color:'#0f172a' }}>
            {isClient ? '📁 My Projects' : '📁 Projects'}
          </h1>
          <p style={{ margin:'3px 0 0', color:'#64748b', fontSize:13 }}>{projects.length} projects total</p>
        </div>
        {isAdmin && (
          <button style={S.btn('primary')} onClick={() => setModal(true)}>
            <FiPlus/> New Project
          </button>
        )}
      </div>

      {/* Stat Cards */}
      <div style={{ display:'flex', gap:12, marginBottom:20, flexWrap:'wrap' }}>
        {statCards.map(card => (
          <div key={card.label} style={{
            background:'#fff', borderRadius:14, padding:'14px 18px',
            border:'1px solid #f1f5f9', boxShadow:'0 1px 6px rgba(0,0,0,0.05)',
            display:'flex', alignItems:'center', gap:12, flex:'1 1 120px',
          }}>
            <div style={{ width:38, height:38, borderRadius:10, background:card.bg, display:'flex', alignItems:'center', justifyContent:'center', color:card.color, flexShrink:0 }}>
              {card.icon}
            </div>
            <div>
              <div style={{ fontSize:22, fontWeight:800, color:'#0f172a', lineHeight:1 }}>{card.value}</div>
              <div style={{ fontSize:11, color:'#64748b', marginTop:2, fontWeight:500 }}>{card.label}</div>
            </div>
          </div>
        ))}
      </div>

      {/* Toolbar */}
      <div style={{ display:'flex', gap:10, marginBottom:12, flexWrap:'wrap', alignItems:'center' }}>
        <div style={{
          display:'flex', alignItems:'center', gap:8,
          background:'#fff', border:'1.5px solid #e2e8f0', borderRadius:10,
          padding:'7px 13px', flex:1, minWidth:180,
        }}>
          <FiSearch color="#94a3b8" size={14}/>
          <input
            value={search} onChange={e=>setSearch(e.target.value)}
            placeholder="Search projects…"
            style={{ border:'none', outline:'none', fontSize:13, flex:1, background:'transparent' }}
          />
          {search && <button onClick={()=>setSearch('')} style={{ background:'none', border:'none', cursor:'pointer', color:'#94a3b8', padding:0, fontSize:16 }}>×</button>}
        </div>
        {/* View toggles */}
        <div style={{ display:'flex', gap:3, background:'#f1f5f9', borderRadius:10, padding:3 }}>
          {[
            { v:'month', icon:'🗓', label:'Monthly' },
            { v:'grid',  icon:<FiGrid size={13}/>,  label:'Grid' },
            { v:'list',  icon:<FiList size={13}/>,  label:'List' },
          ].map(({ v, icon, label }) => (
            <button key={v} onClick={()=>setView(v)} title={label} style={{
              padding:'6px 12px', borderRadius:8, border:'none', cursor:'pointer',
              background: view===v ? '#fff' : 'transparent',
              color: view===v ? '#6366f1' : '#94a3b8',
              fontWeight: view===v ? 700 : 400,
              fontSize:12, boxShadow: view===v ? '0 1px 4px rgba(0,0,0,0.08)' : 'none',
              display:'flex', alignItems:'center', gap:4, transition:'all .15s',
            }}>
              {icon} {label}
            </button>
          ))}
        </div>
      </div>

      {/* Status Filters */}
      <div style={{ display:'flex', gap:6, marginBottom:20, flexWrap:'wrap' }}>
        {[
          { value:'all',        label:`All (${projects.length})` },
          { value:'active',     label:'Active' },
          { value:'planning',   label:'Planning' },
          { value:'on-hold',    label:'On-Hold' },
          { value:'completed',  label:'Completed' },
          { value:'cancelled',  label:'Cancelled' },
          { value:'internal',   label:'🏢 Internal' },
          { value:'this-week',  label:'This Week' },
          { value:'this-month', label:'This Month' },
        ].map(({ value, label }) => (
          <button key={value} onClick={()=>setFilter(value)} style={{
            padding:'5px 14px', borderRadius:20,
            border:`1.5px solid ${filter===value ? 'var(--pm-primary)' : '#e2e8f0'}`,
            background: filter===value ? 'var(--pm-primary)' : '#fff',
            color: filter===value ? '#fff' : '#475569',
            fontWeight:600, fontSize:12, cursor:'pointer', transition:'all .15s',
          }}>{label}</button>
        ))}
      </div>

      {/* ── MONTHLY VIEW ── */}
      {view === 'month' && (
        <div style={{ display:'flex', flexDirection:'column', gap:28 }}>
          {byMonth.length === 0 && (
            <div style={{ textAlign:'center', padding:'60px 0', color:'#94a3b8' }}>
              <FiFolder size={48} style={{ opacity:.2, marginBottom:12 }}/>
              <p style={{ fontSize:15, margin:0 }}>No projects found</p>
            </div>
          )}
          {byMonth.map(({ label, projects: mProjects }) => (
            <div key={label}>
              {/* Month header */}
              <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:12 }}>
                <div style={{
                  background:'var(--pm-gradient)', color:'#fff',
                  padding:'4px 14px', borderRadius:20, fontSize:13, fontWeight:700,
                }}>
                  {label}
                </div>
                <span style={{ fontSize:12, color:'#94a3b8', fontWeight:500 }}>
                  {mProjects.length} project{mProjects.length!==1?'s':''}
                </span>
                <div style={{ flex:1, height:1, background:'#f1f5f9' }}/>
                {/* month mini stats */}
                <div style={{ display:'flex', gap:8, fontSize:11 }}>
                  {['active','completed','on-hold'].map(s => {
                    const cnt = mProjects.filter(p=>p.status===s).length;
                    if (!cnt) return null;
                    const sc = STATUS_COLORS[s];
                    return (
                      <span key={s} style={{ background:sc.bg, color:sc.txt, padding:'2px 8px', borderRadius:10, fontWeight:600, textTransform:'capitalize' }}>
                        {cnt} {s}
                      </span>
                    );
                  })}
                </div>
              </div>

              {/* Project rows */}
              <div style={{ background:'#fff', borderRadius:14, border:'1px solid #f1f5f9', overflow:'hidden', boxShadow:'0 1px 6px rgba(0,0,0,0.04)' }}>
                {mProjects.map((p, idx) => {
                  const sc = STATUS_COLORS[p.status] || STATUS_COLORS.planning;
                  const pct = p.progress || 0;
                  const isOverdue = p.dueDate && new Date(p.dueDate) < now && !['completed','cancelled'].includes(p.status);
                  const pc = PRIORITY_COLORS[p.priority] || PRIORITY_COLORS.medium;

                  return (
                    <Link
                      key={p._id}
                      to={`/projects/${p._id}`}
                      className="proj-row"
                      style={{
                        display:'grid',
                        gridTemplateColumns: '2.5fr 100px 90px 140px 90px 1fr',
                        alignItems:'center', gap:12, padding:'13px 18px',
                        borderBottom: idx < mProjects.length-1 ? '1px solid #f8fafc' : 'none',
                        textDecoration:'none', color:'inherit',
                        background:'#fff', transition:'background .15s',
                      }}
                    >
                      {/* Name + desc */}
                      <div style={{ minWidth:0 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                          <div style={{
                            width:30, height:30, background:'var(--pm-gradient)',
                            borderRadius:8, display:'flex', alignItems:'center',
                            justifyContent:'center', color:'#fff', flexShrink:0,
                          }}>
                            <FiFolder size={13}/>
                          </div>
                          <div style={{ minWidth:0 }}>
                            <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                              <span style={{ fontWeight:700, color:'#0f172a', fontSize:14, whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                                {p.name}
                              </span>
                              {!p.clientId && (
                                <span style={{ background:'#f1f5f9', color:'#475569', padding:'1px 7px', borderRadius:6, fontSize:10, fontWeight:700, whiteSpace:'nowrap', flexShrink:0 }}>
                                  🏢 Internal
                                </span>
                              )}
                            </div>
                            {p.description && (
                              <div style={{ fontSize:11, color:'#94a3b8', whiteSpace:'nowrap', overflow:'hidden', textOverflow:'ellipsis' }}>
                                {p.description}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Status */}
                      <div>
                        <span style={{
                          background:sc.bg, color:sc.txt,
                          padding:'3px 9px', borderRadius:20,
                          fontSize:10, fontWeight:700, textTransform:'uppercase',
                        }}>{p.status}</span>
                      </div>

                      {/* Priority */}
                      <div>
                        <span style={{
                          background:`${pc}18`, color:pc,
                          padding:'3px 9px', borderRadius:20,
                          fontSize:10, fontWeight:700, textTransform:'capitalize',
                        }}>{p.priority||'medium'}</span>
                      </div>

                      {/* Team */}
                      <div><TeamAvatars members={p.teamMembers}/></div>

                      {/* Due date */}
                      <div style={{ fontSize:11, color: isOverdue ? '#ef4444' : '#64748b', fontWeight: isOverdue ? 700 : 400, whiteSpace:'nowrap' }}>
                        {p.dueDate
                          ? <>📅 {new Date(p.dueDate).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}{isOverdue&&' ⚠️'}</>
                          : <span style={{ color:'#cbd5e1' }}>No due date</span>}
                      </div>

                      {/* Progress */}
                      <div style={{ display:'flex', alignItems:'center', gap:8 }}>
                        <div style={{ flex:1, height:5, background:'#f1f5f9', borderRadius:3, overflow:'hidden' }}>
                          <div style={{
                            height:'100%', width:`${pct}%`,
                            background: pct===100 ? 'linear-gradient(90deg,#10b981,#059669)' : 'var(--pm-gradient)',
                            borderRadius:3,
                          }}/>
                        </div>
                        <span style={{ fontSize:11, color:'#64748b', fontWeight:700, width:28, textAlign:'right', flexShrink:0 }}>{pct}%</span>
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── GRID VIEW ── */}
      {view === 'grid' && (
        <div style={{
          display:'grid',
          gridTemplateColumns: filtered.length===1 ? '1fr' : filtered.length===2 ? 'repeat(2,1fr)' : 'repeat(auto-fill,minmax(300px,1fr))',
          gap:18,
        }}>
          {filtered.map(p => {
            const sc = STATUS_COLORS[p.status] || STATUS_COLORS.planning;
            const pct = p.progress || 0;
            const isOverdue = p.dueDate && new Date(p.dueDate) < now && !['completed','cancelled'].includes(p.status);
            const pc = PRIORITY_COLORS[p.priority] || PRIORITY_COLORS.medium;
            return (
              <Link to={`/projects/${p._id}`} key={p._id} style={{
                background:'#fff', borderRadius:16, padding:22,
                boxShadow:'0 2px 10px rgba(0,0,0,0.05)',
                border:'1px solid #f1f5f9', textDecoration:'none', color:'inherit',
                display:'flex', flexDirection:'column', transition:'all .2s',
              }}
                onMouseEnter={e=>{ e.currentTarget.style.transform='translateY(-3px)'; e.currentTarget.style.boxShadow='0 12px 28px rgba(0,0,0,0.1)'; }}
                onMouseLeave={e=>{ e.currentTarget.style.transform='translateY(0)'; e.currentTarget.style.boxShadow='0 2px 10px rgba(0,0,0,0.05)'; }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:14 }}>
                  <div style={{ width:42, height:42, background:'var(--pm-gradient)', borderRadius:11, display:'flex', alignItems:'center', justifyContent:'center', color:'#fff', boxShadow:'0 4px 10px rgba(99,102,241,.3)' }}>
                    <FiFolder size={18}/>
                  </div>
                  <div style={{ display:'flex', gap:5, alignItems:'center' }}>
                    {isOverdue && <span style={{ background:'#fee2e2', color:'#991b1b', padding:'2px 7px', borderRadius:7, fontSize:9, fontWeight:700 }}>OVERDUE</span>}
                    <span style={{ background:sc.bg, color:sc.txt, padding:'3px 10px', borderRadius:20, fontSize:10, fontWeight:700, textTransform:'uppercase' }}>{p.status}</span>
                  </div>
                </div>
                <div style={{ display:'flex', alignItems:'center', gap:7, marginBottom:5 }}>
                  <h3 style={{ margin:0, fontSize:15, fontWeight:700, color:'#0f172a' }}>{p.name}</h3>
                  {!p.clientId && <span style={{ background:'#f1f5f9', color:'#475569', padding:'1px 7px', borderRadius:6, fontSize:10, fontWeight:700, flexShrink:0 }}>🏢 Internal</span>}
                </div>
                <p style={{ margin:'0 0 14px', fontSize:12, color:'#64748b', lineHeight:1.5, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden', flex:1 }}>
                  {p.description || 'No description.'}
                </p>
                <div style={{ display:'flex', gap:8, fontSize:11, marginBottom:12, flexWrap:'wrap' }}>
                  <span style={{ background:`${pc}18`, color:pc, padding:'2px 7px', borderRadius:6, fontWeight:600, textTransform:'capitalize' }}>{p.priority||'medium'}</span>
                  <span style={{ color:'#64748b' }}>✅ {p.taskCount??0} tasks</span>
                  {p.dueDate && <span style={{ color: isOverdue?'#ef4444':'#94a3b8' }}>📅 {new Date(p.dueDate).toLocaleDateString('en-IN',{day:'numeric',month:'short'})}</span>}
                </div>
                <div style={{ marginBottom:12 }}><TeamAvatars members={p.teamMembers} limit={4}/></div>
                <div>
                  <div style={{ display:'flex', justifyContent:'space-between', fontSize:10, color:'#94a3b8', marginBottom:5 }}>
                    <span>Progress</span>
                    <span style={{ fontWeight:700, color:pct===100?'#10b981':'#64748b' }}>{pct}%</span>
                  </div>
                  <div style={{ height:6, background:'#f1f5f9', borderRadius:3, overflow:'hidden' }}>
                    <div style={{ height:'100%', width:`${pct}%`, background:pct===100?'linear-gradient(90deg,#10b981,#059669)':'var(--pm-gradient)', borderRadius:3 }}/>
                  </div>
                </div>
              </Link>
            );
          })}
          {filtered.length===0 && (
            <div style={{ gridColumn:'1/-1', textAlign:'center', padding:'60px 0', color:'#94a3b8' }}>
              <FiFolder size={48} style={{ opacity:.2, marginBottom:12 }}/>
              <p style={{ fontSize:15, margin:'0 0 14px' }}>No projects found</p>
              {isAdmin && <button style={S.btn('primary')} onClick={()=>setModal(true)}><FiPlus/> Create First Project</button>}
            </div>
          )}
        </div>
      )}

      {/* ── LIST VIEW ── */}
      {view === 'list' && (
        <div style={{ background:'#fff', borderRadius:14, boxShadow:'0 2px 10px rgba(0,0,0,0.05)', border:'1px solid #f1f5f9', overflow:'hidden' }}>
          <table style={{ width:'100%', borderCollapse:'collapse', fontSize:13 }}>
            <thead>
              <tr style={{ background:'#f8fafc' }}>
                {['Project','Status','Priority','Team','Due Date','Progress'].map(h=>(
                  <th key={h} style={{ padding:'11px 15px', textAlign:'left', fontWeight:700, color:'#64748b', fontSize:11, textTransform:'uppercase', letterSpacing:'0.04em', borderBottom:'2px solid #f1f5f9' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => {
                const sc = STATUS_COLORS[p.status]||STATUS_COLORS.planning;
                const isOverdue = p.dueDate&&new Date(p.dueDate)<now&&!['completed','cancelled'].includes(p.status);
                return (
                  <tr key={p._id} style={{ borderBottom:'1px solid #f8fafc', cursor:'pointer' }}
                    onClick={()=>window.location.href=`/projects/${p._id}`}
                    onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'}
                    onMouseLeave={e=>e.currentTarget.style.background='#fff'}>
                    <td style={{ padding:'13px 15px' }}>
                      <div style={{ fontWeight:700, color:'#0f172a' }}>{p.name}</div>
                      {p.description&&<div style={{ fontSize:11, color:'#94a3b8', marginTop:1, maxWidth:240, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{p.description}</div>}
                    </td>
                    <td style={{ padding:'13px 15px' }}>
                      <span style={{ background:sc.bg, color:sc.txt, padding:'3px 9px', borderRadius:20, fontSize:10, fontWeight:700, textTransform:'uppercase' }}>{p.status}</span>
                    </td>
                    <td style={{ padding:'13px 15px' }}>
                      <span style={{ color:PRIORITY_COLORS[p.priority]||'#f59e0b', fontWeight:600, textTransform:'capitalize', fontSize:12 }}>{p.priority||'medium'}</span>
                    </td>
                    <td style={{ padding:'13px 15px' }}><TeamAvatars members={p.teamMembers}/></td>
                    <td style={{ padding:'13px 15px', color:isOverdue?'#ef4444':'#64748b', fontWeight:isOverdue?600:400, fontSize:12 }}>
                      {p.dueDate?new Date(p.dueDate).toLocaleDateString('en-IN',{day:'numeric',month:'short',year:'numeric'}):'—'}
                      {isOverdue&&' ⚠️'}
                    </td>
                    <td style={{ padding:'13px 15px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                        <div style={{ flex:1, height:5, background:'#f1f5f9', borderRadius:3, overflow:'hidden', minWidth:70 }}>
                          <div style={{ height:'100%', width:`${p.progress||0}%`, background:'var(--pm-gradient)', borderRadius:3 }}/>
                        </div>
                        <span style={{ fontSize:11, color:'#64748b', fontWeight:600, width:28, textAlign:'right' }}>{p.progress||0}%</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length===0&&<tr><td colSpan={6} style={{ textAlign:'center', padding:48, color:'#94a3b8' }}>No projects found</td></tr>}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Create Modal ── */}
      {modal && (
        <div style={S.overlay} onClick={e=>{ if(e.target===e.currentTarget){ setModal(false); setIsInternal(false); } }}>
          <div style={S.modal}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:22 }}>
              <h3 style={{ margin:0, fontSize:18, fontWeight:700 }}>New Project</h3>
              <button style={{ background:'none', border:'none', cursor:'pointer', fontSize:22, color:'#94a3b8' }} onClick={()=>{ setModal(false); setIsInternal(false); }}><FiX/></button>
            </div>
            {/* Internal toggle */}
            <div
              onClick={() => { setIsInternal(v=>!v); setForm(f=>({...f,clientId:''})); }}
              style={{
                display:'flex', alignItems:'center', gap:12, padding:'12px 16px',
                borderRadius:12, border:`2px solid ${isInternal?'#6366f1':'#e2e8f0'}`,
                background: isInternal ? '#eef2ff' : '#f8fafc',
                cursor:'pointer', marginBottom:14, transition:'all .2s', userSelect:'none',
              }}>
              <div style={{
                width:38, height:22, borderRadius:11, position:'relative', transition:'all .2s',
                background: isInternal ? '#6366f1' : '#cbd5e1',
              }}>
                <div style={{
                  width:16, height:16, borderRadius:'50%', background:'#fff',
                  position:'absolute', top:3, transition:'all .2s',
                  left: isInternal ? 19 : 3, boxShadow:'0 1px 3px rgba(0,0,0,0.2)',
                }}/>
              </div>
              <div>
                <div style={{ fontWeight:700, fontSize:13, color: isInternal?'#4338ca':'#374151' }}>
                  🏢 Internal / In-house Project
                </div>
                <div style={{ fontSize:11, color:'#64748b', marginTop:1 }}>
                  {isInternal ? 'No client needed — this is an internal company project' : 'Toggle on if this project has no external client'}
                </div>
              </div>
            </div>

            {/* Client section — hidden for internal projects */}
            {!isInternal && (
              <div style={{ background:'#f8fafc', border:'2px solid #e2e8f0', borderRadius:12, padding:14, marginBottom:14 }}>
                <div style={{ fontSize:13, fontWeight:700, color:'#374151', marginBottom:8 }}>👤 Client</div>
                <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                  <label style={{ ...S.label, margin:0 }}>Client Name *
                    <select style={{ ...S.inp, borderColor:!form.clientId?'#f87171':'#e2e8f0' }}
                      value={form.clientId} onChange={e=>setForm({...form,clientId:e.target.value})}>
                      <option value="">— Select —</option>
                      {clients.map(c=><option key={c._id} value={c._id}>{c.name}{c.company?` (${c.company})`:''}</option>)}
                    </select>
                  </label>
                  <label style={{ ...S.label, margin:0 }}>Client Type
                    {(() => {
                      const sel = clients.find(c=>String(c._id)===String(form.clientId));
                      return sel?.clientType
                        ? <div style={{ ...S.inp, background:'#fff', display:'flex', alignItems:'center', gap:8, color:'#059669', fontWeight:600 }}>
                            <span>{CLIENT_TYPE_ICONS[sel.clientType]}</span>
                            <span style={{ fontSize:12 }}>{CLIENT_TYPE_LABELS[sel.clientType]}</span>
                          </div>
                        : <div style={{ ...S.inp, background:'#f1f5f9', color:'#94a3b8', fontStyle:'italic', fontSize:12 }}>
                            {form.clientId?'No type set':'Select client first'}
                          </div>;
                    })()}
                  </label>
                </div>
                {clients.length===0&&<div style={{ fontSize:12, color:'#f59e0b', marginTop:6 }}>No clients found. Add one in Team page first.</div>}
              </div>
            )}
            <label style={S.label}>Project Name *
              <input style={S.inp} value={form.name} onChange={e=>setForm({...form,name:e.target.value})} placeholder="e.g. Website Redesign"/>
            </label>
            <label style={S.label}>Description
              <textarea style={{ ...S.inp, resize:'vertical', minHeight:64 }} value={form.description} onChange={e=>setForm({...form,description:e.target.value})} rows={3}/>
            </label>
            <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
              <label style={S.label}>Type
                <select style={S.inp} value={form.projectType} onChange={e=>setForm({...form,projectType:e.target.value})}>
                  {['web','mobile','design','seo','marketing','other'].map(t=><option key={t} value={t}>{t.charAt(0).toUpperCase()+t.slice(1)}</option>)}
                </select>
              </label>
              <label style={S.label}>Status
                <select style={S.inp} value={form.status} onChange={e=>setForm({...form,status:e.target.value})}>
                  {['planning','active','on-hold'].map(s=><option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
                </select>
              </label>
              <label style={S.label}>Priority
                <select style={S.inp} value={form.priority} onChange={e=>setForm({...form,priority:e.target.value})}>
                  {['low','medium','high'].map(p=><option key={p} value={p}>{p.charAt(0).toUpperCase()+p.slice(1)}</option>)}
                </select>
              </label>
              <label style={S.label}>Budget
                <input type="number" style={S.inp} value={form.budget} onChange={e=>setForm({...form,budget:e.target.value})} placeholder="0"/>
              </label>
              <label style={S.label}>Start Date
                <input type="date" style={S.inp} value={form.startDate} onChange={e=>setForm({...form,startDate:e.target.value})}/>
              </label>
              <label style={S.label}>Due Date
                <input type="date" style={S.inp} value={form.dueDate} onChange={e=>setForm({...form,dueDate:e.target.value})}/>
              </label>
            </div>
            <div style={{ display:'flex', gap:10, justifyContent:'flex-end', marginTop:8 }}>
              <button style={S.btn('ghost')} onClick={()=>setModal(false)}>Cancel</button>
              <button style={S.btn('primary')} onClick={createProject}><FiPlus/> Create</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
